import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import DictionaryApiService from "../../../admin/system/integrate/DictionaryApiService";
import WiktionaryAttribution from "../../../admin/pzl_common/WiktionaryAttribution";
import { wwPlayColors } from "../lib/theme";

const PART_OF_SPEECH_ORDER = [
    "nom", "noun", "verbe", "verb", "adjectif", "adjective", "adverbe", "adverb", "title",
];

function formatDefinition(definition) {
    if (typeof definition !== "string") return String(definition ?? "");
    return definition.replace(/(\d+\.\s)/g, "\n$1").trim();
}

function titleCasePartOfSpeech(value) {
    if (!value) return "";
    return value.charAt(0).toUpperCase() + (value.slice(1) || "").toLowerCase();
}

function HiddenWordCircles({ length, compact = false }) {
    if (!length || length < 1) return null;
    const size = compact ? 16 : "max(22px, 5.5vw)";
    return (
        <Box
            role="img"
            aria-label={`${length} letters`}
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: compact ? 0.45 : 0.75,
                alignItems: "center",
                py: 0.25,
            }}
        >
            {Array.from({ length }, (_, index) => (
                <Box
                    key={index}
                    sx={{
                        width: size,
                        height: size,
                        borderRadius: "50%",
                        border: compact ? "1.5px solid #059669" : "2px solid #059669",
                        bgcolor: "rgba(255, 255, 255, 0.6)",
                        flexShrink: 0,
                    }}
                />
            ))}
        </Box>
    );
}

/**
 * Mobile / iOS dictionary sheet for Word Wheel — isolated from web `WordMeaningDialog`.
 * Pass `contained` to scope the sheet to a positioned parent (e.g. 9:16 recording phone).
 */
export default function WordWheelDictionarySheet({
    open,
    onClose,
    word,
    wordRevealed = true,
    language = "english",
    colors = wwPlayColors,
    /** When true, drawer fills its positioned parent instead of the viewport. */
    contained = false,
}) {
    const [wordData, setWordData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;

        const fetchDefinition = async () => {
            const trimmed = (word || "").trim();
            if (!open || !trimmed) {
                setWordData(null);
                setError("");
                return;
            }

            setLoading(true);
            setError("");
            try {
                const result = await DictionaryApiService.getWordDefinition(trimmed, language);
                if (cancelled) return;
                setWordData(result?.length > 0 ? result[0] : null);
            } catch {
                if (!cancelled) {
                    setError("Could not load definition.");
                    setWordData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchDefinition();
        return () => { cancelled = true; };
    }, [open, word, language]);

    const handleClose = () => {
        setWordData(null);
        setError("");
        onClose?.();
    };

    const meanings = wordData?.meanings || [];
    const trimmedWord = (word || "").trim();
    const displayWord = trimmedWord
        ? trimmedWord.charAt(0).toUpperCase() + trimmedWord.slice(1).toLowerCase()
        : "";

    return (
        <Drawer
            anchor="bottom"
            open={open}
            onClose={handleClose}
            disablePortal={contained}
            ModalProps={{
                keepMounted: false,
                disableScrollLock: contained,
                ...(contained
                    ? {
                          sx: {
                              position: "absolute",
                              inset: 0,
                          },
                      }
                    : {}),
            }}
            sx={
                contained
                    ? {
                          position: "absolute",
                          inset: 0,
                          width: "100%",
                          height: "100%",
                          zIndex: 20,
                          "& .MuiBackdrop-root": {
                              position: "absolute",
                          },
                      }
                    : undefined
            }
            PaperProps={{
                sx: {
                    borderTopLeftRadius: 20,
                    borderTopRightRadius: 20,
                    bgcolor: colors.surface,
                    backgroundImage: "none",
                    ...(contained
                        ? {
                              position: "absolute",
                              left: 0,
                              right: 0,
                              bottom: 0,
                              width: "100%",
                              maxWidth: "100%",
                              maxHeight: "78%",
                          }
                        : {
                              maxHeight: "min(78dvh, 640px)",
                          }),
                },
            }}
        >
            <Box
                sx={{
                    px: 2,
                    pt: 1.25,
                    pb: "max(16px, env(safe-area-inset-bottom, 0px))",
                    color: colors.textOnSurface || colors.text,
                }}
            >
                <Box
                    sx={{
                        width: 40,
                        height: 4,
                        borderRadius: 999,
                        bgcolor: "rgba(15, 61, 54, 0.18)",
                        mx: "auto",
                        mb: 1.25,
                    }}
                />

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 1.5,
                        minHeight: 44,
                    }}
                >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        {trimmedWord && (
                            wordRevealed ? (
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: 800,
                                        color: "#059669",
                                        lineHeight: 1.25,
                                        textTransform: "capitalize",
                                    }}
                                >
                                    {displayWord}
                                </Typography>
                            ) : (
                                <HiddenWordCircles length={trimmedWord.length} compact={contained} />
                            )
                        )}
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        aria-label="Close dictionary"
                        sx={{ minWidth: 44, minHeight: 44, color: colors.textOnSurface || colors.text }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        overflowY: "auto",
                        maxHeight: contained ? "min(48%, 420px)" : "min(58dvh, 520px)",
                        pr: 0.5,
                    }}
                >
                    {loading && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
                            <CircularProgress size={22} sx={{ color: "#059669" }} />
                            <Typography variant="body2" sx={{ color: "rgba(15, 61, 54, 0.7)" }}>
                                Loading definition…
                            </Typography>
                        </Box>
                    )}

                    {!loading && error && (
                        <Typography variant="body2" sx={{ color: "#b91c1c", py: 1 }}>
                            {error}
                        </Typography>
                    )}

                    {!loading && !error && meanings.length === 0 && (
                        <Typography variant="body2" sx={{ color: "rgba(15, 61, 54, 0.7)", py: 1 }}>
                            No definitions found.
                        </Typography>
                    )}

                    {!loading && !error && meanings.length > 0 && (
                        <Box sx={{ pb: 1 }}>
                            {[...meanings]
                                .sort((a, b) => {
                                    const ai = PART_OF_SPEECH_ORDER.indexOf((a.partOfSpeech || "").toLowerCase());
                                    const bi = PART_OF_SPEECH_ORDER.indexOf((b.partOfSpeech || "").toLowerCase());
                                    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                                })
                                .map((meaning, meaningIndex) => (
                                    <Box key={`${meaning.partOfSpeech}-${meaningIndex}`} sx={{ mb: 2.25 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 800,
                                                color: "#047857",
                                                mb: 0.75,
                                                textTransform: "capitalize",
                                            }}
                                        >
                                            {titleCasePartOfSpeech(meaning.partOfSpeech)}
                                        </Typography>
                                        {(meaning.definitions || []).map((def, defIndex) => (
                                            <Box key={defIndex} sx={{ mb: 1.25, pl: 0.5 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        whiteSpace: "pre-line",
                                                        lineHeight: 1.6,
                                                        color: colors.textOnSurface || colors.text,
                                                    }}
                                                >
                                                    {formatDefinition(def.definition)}
                                                </Typography>
                                                {def.example && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: "block",
                                                            mt: 0.5,
                                                            pl: 1,
                                                            fontStyle: "italic",
                                                            color: "rgba(15, 61, 54, 0.65)",
                                                            borderLeft: "2px solid rgba(5, 150, 105, 0.35)",
                                                        }}
                                                    >
                                                        &ldquo;{def.example}&rdquo;
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                    </Box>
                                ))}
                        </Box>
                    )}
                </Box>

                <WiktionaryAttribution />
            </Box>
        </Drawer>
    );
}
